import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { UploadInvoiceResponse } from './types';

/**
 * Hook to upload an invoice document (PDF, JPG, JPEG) to POST /api/v2/invoices/upload/
 */
export function useUploadInvoice() {
  const queryClient = useQueryClient();

  return useMutation<UploadInvoiceResponse, Error, File>({
    mutationFn: async (file: File) => {
      // Validate file extension (exclude PNG per backend validator)
      const allowedExts = ['pdf', 'jpg', 'jpeg'];
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!allowedExts.includes(ext)) {
        throw new Error('Unsupported format. Please upload PDF, JPG, or JPEG.');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<UploadInvoiceResponse>(
        '/v2/invoices/upload/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 60_000, // 60 seconds timeout for processing
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
